import React, { useState, useEffect, useMemo, useRef } from "react";
import { Select, DatePicker, Input, Button, Divider, Table } from "antd";
import { MenuOutlined, DeleteOutlined, PlusOutlined, UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import api from "@/utils/api";
import http from "@/utils/http";
import commonFn from "@/utils/commonFn";
import UploadToExcelModal from "./component/uploadExcelModal";
const { TextArea } = Input;

let rowIdSeed = 0;
const genRowId = () => `row_${Date.now()}_${rowIdSeed++}`;

const DATE_FORMAT = "MM/DD/YYYY";

const JournalEntryPage = () => {
  const [rows, setRows] = useState([]);
  const [propOptionsMap, setPropOptionsMap] = useState({});
  const [propLoadingMap, setPropLoadingMap] = useState({});
  const [glAcctOptionsMap, setGlAcctOptionsMap] = useState({});
  const [glAcctLoadingMap, setGlAcctLoadingMap] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [isDirty, setIsDirty] = useState(true); // 初始默认可提交（默认已有一行空数据）

  const UploadToExcelModalRef = useRef(null);
  const initializedRef = useRef(false);

  const propIdFun = async (rowId, content = "") => {
    try {
      setPropLoadingMap((prev) => ({ ...prev, [rowId]: true }));
      const response = await http.get(api.propSearch, { params: { content } });
      const data = response?.data || [];
      const formattedData = data.map((item) => ({
        value: item.propId,
        label: item.propName,
        prop: item.prop,
      }));
      setPropOptionsMap((prev) => ({ ...prev, [rowId]: formattedData }));
    } catch (err) {
      console.log(err);
    } finally {
      setPropLoadingMap((prev) => ({ ...prev, [rowId]: false }));
    }
  };

  const glAcctByPropIdFun = async (rowId, propId) => {
    if (!propId) {
      setGlAcctOptionsMap((prev) => ({ ...prev, [rowId]: [] }));
      return;
    }
    try {
      setGlAcctLoadingMap((prev) => ({ ...prev, [rowId]: true }));
      const response = await http.get(api.glAcctByPropIdList, {
        params: { propId },
      });
      const data = response?.data || [];
      const formattedData = data.map((item) => ({
        value: item.glAcct,
        label: item.glAcctName,
        title: item.title,
      }));
      setGlAcctOptionsMap((prev) => ({ ...prev, [rowId]: formattedData }));
    } catch (err) {
      console.log(err);
    } finally {
      setGlAcctLoadingMap((prev) => ({ ...prev, [rowId]: false }));
    }
  };

  const handleAddRow = () => {
    const newId = genRowId();
    setRows((prev) => [
      ...prev,
      {
        id: newId,
        propId: undefined,
        prop: undefined,
        glAcct: undefined,
        date1: null,
        amount: undefined,
        remark: "",
      },
    ]);
    propIdFun(newId);
    setIsDirty(true);
  };

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    handleAddRow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteRow = (rowId) => {
    setRows((prev) => prev.filter((r) => r.id !== rowId));
    setPropOptionsMap((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    setGlAcctOptionsMap((prev) => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
    setIsDirty(true);
  };

  const updateRow = (rowId, field, value) => {
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, [field]: value } : r)));
    setIsDirty(true);
  };

  const handlePropChange = (rowId, value) => {
    const options = propOptionsMap[rowId] || [];
    const selected = options.find((opt) => opt.value === value);
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? {
              ...r,
              propId: value,
              prop: selected ? selected.prop : undefined,
              glAcct: undefined,
            }
          : r
      )
    );
    glAcctByPropIdFun(rowId, value);
    setIsDirty(true);
  };
  const handleGlAcctChange = (rowId, value) => {
    const options = glAcctOptionsMap[rowId] || [];
    const selected = options.find((opt) => opt.value === value);
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId
          ? {
              ...r,
              glAcct: value,
              remark: selected?.title ?? r.remark, 
            }
          : r
      )
    );
    setIsDirty(true);
  };

  // ------- Total Amount -------
  const totalAmount = useMemo(() => {
    return rows.reduce((sum, r) => {
      const num = Number(r.amount);
      return sum + (isNaN(num) ? 0 : num);
    }, 0);
  }, [rows]);
const handleSubmit = async () => {
  if (rows.length === 0) {
    setSubmitResult({ success: false, msg: "Please add at least one entry" });
    setIsDirty(false);
    return;
  }
  const invalidRow = rows.find(
    (r) =>
      !r.prop ||
      !r.glAcct ||
      !r.date1 ||
      r.amount === undefined ||
      r.amount === null ||
      r.amount === ""
  );
  if (invalidRow) {
    setSubmitResult({
      success: false,
      msg: "Please complete Prop / Gl Acct / Date / Amount for every row",
    });
    setIsDirty(false); // 同上
    return;
  }
  const payload = rows.map((r) => ({
    prop: r.prop,
    glAcct: r.glAcct,
    date1: r.date1 ? dayjs(r.date1).format(DATE_FORMAT) : "",
    amount: Number(r.amount),
    remark: r.remark || "",
  }));
  try {
    setSubmitLoading(true);
    const res = await http.post(api.getJournalEntry, payload);
    const isSuccess = res?.code === "1";
    setSubmitResult({
      success: isSuccess,
      msg: res?.msg || (isSuccess ? "Request succeeded" : "Request failed"),
    });
  } catch (err) {
    console.log(err);
    setSubmitResult({ success: false, msg: "Request failed" });
  } finally {
    setSubmitLoading(false);
    setIsDirty(false); 
  }
};

  const columns = [
    {
      title: "Prop",
      dataIndex: "prop",
      width: 300,
      render: (_, record) => (
        <Select
          style={{ width: "100%" }}
          placeholder="Select Prop"
          value={record.propId}
          options={propOptionsMap[record.id] || []}
          loading={propLoadingMap[record.id]}
          showSearch
          filterOption={false}
          onSearch={(val) => propIdFun(record.id, val)}
          onFocus={() => {
            if (!propOptionsMap[record.id]) propIdFun(record.id);
          }}
          onChange={(val) => handlePropChange(record.id, val)}
        />
      ),
    },
    {
      title: "Gl Acct",
      dataIndex: "glAcct",
      width: 200,
      render: (_, record) => (
        <Select
          style={{ width: "100%" }}
          placeholder="Select Gl Acct"
          value={record.glAcct}
          options={glAcctOptionsMap[record.id] || []}
          loading={glAcctLoadingMap[record.id]}
          disabled={!record.propId}
          showSearch
          onChange={(val) => handleGlAcctChange(record.id, val)}
        />
      ),
    },
    {
      title: "Date",
      dataIndex: "date1",
      width: 160,
      render: (_, record) => (
        <DatePicker
          style={{ width: "100%" }}
          format={DATE_FORMAT}
          value={record.date1}
          onChange={(val) => updateRow(record.id, "date1", val)}
        />
      ),
    },
    {
  title: "Amount",
  dataIndex: "amount",
  width: 140,
  render: (_, record) => (
    <Input
      prefix="$"
      placeholder="Enter Amount"
      value={commonFn.inputAmountFormat(record.amount, false, true)}
      onChange={(e) => {
        const raw = commonFn.inputAmountFormat(e.target.value, true, true);
        updateRow(record.id, "amount", raw);
      }}
    />
  ),
},
    {
      title: "Remark",
      dataIndex: "remark",
      render: (_, record) => (
        <TextArea
          placeholder="Remark"
          rows={1}
          style={{ resize: "vertical" }}
          value={record.remark}
          onChange={(e) => updateRow(record.id, "remark", e.target.value)}
        />
      ),
    },
    {
      title: "",
      dataIndex: "action",
      width: 40,
      render: (_, record) => (
        <DeleteOutlined
          style={{ color: "#F56C6C", cursor: "pointer" }}
          onClick={() => handleDeleteRow(record.id)}
        />
      ),
    },
  ];

  return (
    <div className="fund-transfer-page">
      <div style={{ display: "flex", gap: 16 }}>
        <div style={{ flex: "0 0 60%", minWidth: 0, overflow: "auto" }}>
          <div style={{ marginBottom: 12, display: "flex" }}>
            <Button className="btnNew" icon={<PlusOutlined />} onClick={handleAddRow}>
              Add Journal Entry
            </Button>

            {commonFn.btnAuthorityFlag("UploadExcel") && (
              <Button
                className="btnNew"
                onClick={() => {
                  UploadToExcelModalRef.current?.open({
                    type: "transactions",
                  });
                }}
              >
                <UploadOutlined />
                Upload
              </Button>
            )}
          </div>
          <Divider style={{ borderColor: "#E2E8F0", marginTop: 8, marginBottom: 16 }} />
          <Table
            rowClassName="table-row-gradient"
            columns={columns}
            dataSource={rows}
            rowKey="id"
            className="no-border-table"
            bordered={false}
            showHeader={true}
            pagination={false}
            scroll={{ x: "max-content" }}
            tableLayout="fixed"
            locale={{ emptyText: "No entries yet" }}
          />

          <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}>
            <Button
              type="primary"
              loading={submitLoading}
              disabled={!isDirty}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </div>
        </div>

        {/* Right: Information Detail */}
        <div style={{ flex: "0 0 40%", minWidth: 0 }}>
          <div className="fund-transfer-header">
            <div className="fund-transfer-header-title">
              <MenuOutlined />
              Information Detail
            </div>
          </div>

          <Divider style={{ borderColor: "#E2E8F0", marginTop: 8, marginBottom: 16 }} />

          <div style={{ padding: "8px 4px" }}>
            <span style={{ fontWeight: 600 }}>Total Amount: </span>
            <span>{commonFn.amountToDollarFn(totalAmount)}</span>
          </div>
          {/* 结果提示 */}
          {submitResult && (
            <div
              style={{
                marginTop: 8,
                padding: "8px 12px",
                borderRadius: 4,
                fontSize: 13,
                lineHeight: 1.5,
                borderLeft: `3px solid ${submitResult.success ? "#237804" : "#cf1322"}`,
                background: submitResult.success ? "#f6ffed" : "#fff1f0",
                color: submitResult.success ? "#237804" : "#cf1322",
              }}
            >
              {submitResult.msg}
            </div>
          )}
        </div>
      </div>

      <UploadToExcelModal ref={UploadToExcelModalRef} />
    </div>
  );
};

export default JournalEntryPage;